import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

// Fallback wenn ein Status (z.B. nach Löschung) nicht mehr im Tenant existiert
const FALLBACK_STATUS = { label: '—', icon: '•', color: '#6b7280', isFinal: false };

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('de-DE');
  } catch { return String(d); }
}

function dueClass(due, isFinal) {
  if (isFinal) return '';
  if (!due) return '';
  const t = new Date(due).getTime();
  const today = Date.now();
  if (t < today - 86400000) return 'overdue';
  if (t < today + 2 * 86400000) return 'soon';
  return '';
}

export default function Tasks({ onBack }) {
  const { user, refreshUser } = useAuth();
  const { tenantFetch } = useTenant();

  // Tabs: 'mine' | 'team' | 'sub' | 'all'
  // 'sub' nur sichtbar, wenn der User Lead von einer Abteilung mit Sub-Abteilungen ist.
  // 'all' nur sichtbar, wenn user.canViewAllTasks (oder globaler Admin als Fallback).
  const [scope, setScope] = useState('mine');
  const [statuses, setStatuses] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null); // null = noch nicht initialisiert
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewTask, setShowNewTask] = useState(false);

  const statusByKey = Object.fromEntries(statuses.map(s => [s.key, s]));
  // 'list' (Standard) | 'kanban'. Persistiert. Toggle nur in Meine/Team-Tabs sichtbar.
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem('tasks.viewMode') === 'kanban' ? 'kanban' : 'list'
  );
  const setViewModeAndPersist = (mode) => {
    setViewMode(mode);
    localStorage.setItem('tasks.viewMode', mode);
  };
  const canUseKanban = scope === 'mine' || scope === 'team';
  const isKanban = viewMode === 'kanban' && canUseKanban;
  // Detail-Modal: für Kanban-Karten (in Liste hat man Inline-Expand)
  const [detailTask, setDetailTask] = useState(null);

  // Beim Öffnen der Tasks-Seite einmal das User-Objekt aktualisieren —
  // damit gerade neu vergebene Rechte (z.B. canViewAllTasks) sofort greifen
  // ohne dass der User sich aus- und einloggen muss.
  useEffect(() => {
    // Nur einmal beim Mount — refreshUser ist stable über AuthContext.
    refreshUser?.();
  }, []);

  const canSeeSub = (counts.sub ?? 0) > 0;
  // Globaler Admin sieht 'Alle Aufgaben' immer — auch wenn das DB-Feld
  // canViewAllTasks bei alten Accounts noch fehlt.
  const canSeeAll = !!user?.canViewAllTasks || user?.role === 'admin';

  const loadCounts = useCallback(async () => {
    try {
      const res = await tenantFetch('/api/tasks/counts');
      const data = await res.json();
      if (res.ok) setCounts(data || {});
    } catch { /* ignore */ }
  }, [tenantFetch]);

  const loadStatuses = useCallback(async () => {
    try {
      const res = await tenantFetch('/api/task-statuses');
      const data = await res.json();
      if (res.ok) {
        const list = data.statuses || [];
        setStatuses(list);
        // Default-Filter: alle nicht-finalen Status sind angehakt
        setStatusFilter(prev => prev ?? list.filter(s => !s.isFinal).map(s => s.key));
      }
    } catch { /* ignore */ }
  }, [tenantFetch]);

  const load = useCallback(async () => {
    if (statusFilter === null) return; // erst nach Status-Init laden
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('scope', scope);
      // Im Kanban-Mode wollen wir alle Stati zeigen (jede Spalte ist ein Status).
      // Status-Filter im List-Mode bleibt unangetastet.
      if (!isKanban && statusFilter.length) params.set('status', statusFilter.join(','));
      const res = await tenantFetch(`/api/tasks?${params}`);
      const data = await res.json();
      if (res.ok) setTasks(data.tasks || []);
      else setTasks([]);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [tenantFetch, scope, statusFilter, isKanban]);

  useEffect(() => { loadStatuses(); }, [loadStatuses]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { load(); }, [load]);

  // Minütliches Auto-Polling — nur in der gewünschten Konstellation:
  //   - Tab 'Meine' aktiv
  //   - Filter enthält 'open' (offene Aufgaben)
  // Sonst kein Polling — bei 'Erledigt'-Ansicht oder Team-Listen ist es nicht
  // sinnvoll, weil dort nichts spontan neu reinkommt was den User direkt angeht.
  // Tab-Wechsel bricht das Intervall ab; counts werden mit aktualisiert,
  // damit das Tab-Badge live mitzieht.
  useEffect(() => {
    const shouldPoll = scope === 'mine' && statusFilter && statusFilter.length > 0;
    if (!shouldPoll) return;
    const id = setInterval(() => {
      load();
      loadCounts();
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [scope, statusFilter, load, loadCounts]);

  const toggleStatus = (s) => {
    setStatusFilter(prev => {
      const cur = prev || [];
      return cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s];
    });
  };

  const changeStatus = async (taskId, newStatus) => {
    // Optimistisches Update — Karte wechselt SOFORT die Spalte. Falls der
    // Server ablehnt, rollen wir zurück und zeigen den Fehler.
    const previous = tasks;
    const targetInfo = statusByKey[newStatus];
    const isClosing = !!(targetInfo && targetInfo.isFinal);
    setTasks(prev => prev.map(t => t._id === taskId
      ? { ...t, status: newStatus, closedAt: isClosing ? new Date().toISOString() : null }
      : t
    ));
    try {
      const res = await tenantFetch(`/api/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Status-Update fehlgeschlagen');
      // Server-Stand übernehmen (z. B. wenn er closedBy etc. ergänzt hat)
      if (data.task) {
        setTasks(prev => prev.map(t => t._id === taskId ? { ...t, ...data.task } : t));
      }
      loadCounts();
    } catch (err) {
      console.error('[tasks] status change failed:', err);
      setTasks(previous); // Rollback
      alert(err.message);
    }
  };

  const reassignTask = async (taskId, body) => {
    try {
      const res = await tenantFetch(`/api/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      if (data.task) setTasks(prev => prev.map(t => t._id === taskId ? { ...t, ...data.task } : t));
      loadCounts();
    } catch (err) {
      alert(err.message);
      throw err;
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Aufgabe löschen?')) return;
    try {
      const res = await tenantFetch(`/api/tasks/${taskId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setTasks(prev => prev.filter(t => t._id !== taskId));
      loadCounts();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <button className="icon-btn" onClick={onBack} title="Zurück">←</button>
        <h2>📋 Aufgaben</h2>
        {canUseKanban && (
          <div className="tasks-view-toggle" role="group" aria-label="Ansicht wechseln">
            <button
              className={`tasks-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewModeAndPersist('list')}
              title="Liste"
            >📋 Liste</button>
            <button
              className={`tasks-view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewModeAndPersist('kanban')}
              title="Kanban-Board"
            >📊 Kanban</button>
          </div>
        )}
        <button className="primary-btn" onClick={() => setShowNewTask(true)}>+ Neue Aufgabe</button>
      </div>

      <div className="tasks-tabs">
        <TabBtn active={scope === 'mine'} onClick={() => setScope('mine')}
          label="Meine" badge={counts.mine ?? 0} />
        <TabBtn active={scope === 'team'} onClick={() => setScope('team')}
          label="Mein Team" badge={counts.team ?? 0} />
        {canSeeSub && (
          <TabBtn active={scope === 'sub'} onClick={() => setScope('sub')}
            label="Unter-Abteilungen" badge={counts.sub ?? 0} hint="Aufgaben, die einer Unter-Abteilung deiner Lead-Abteilung zugewiesen sind" />
        )}
        {canSeeAll && (
          <TabBtn active={scope === 'all'} onClick={() => setScope('all')}
            label="Alle Aufgaben" badge={counts.all ?? 0}
            hint={'Alle Aufgaben des Mandanten — sichtbar weil du das Recht „Alle Aufgaben sehen" hast (oder globaler Admin bist)'} />
        )}
      </div>

      {!isKanban && (
        <div className="tasks-status-filter">
          <span className="tasks-status-label">Status:</span>
          {statuses.map(info => (
            <label key={info.key} className="tasks-status-chip">
              <input
                type="checkbox"
                checked={(statusFilter || []).includes(info.key)}
                onChange={() => toggleStatus(info.key)}
              />
              <span style={{ color: info.color }}>{info.icon} {info.label}</span>
            </label>
          ))}
        </div>
      )}

      {isKanban ? (
        <KanbanBoard
          tasks={tasks}
          statuses={statuses}
          loading={loading}
          currentUser={user}
          onChangeStatus={changeStatus}
          onReassign={reassignTask}
          onDelete={deleteTask}
          onOpen={(t) => setDetailTask(t)}
        />
      ) : (
      <div className="tasks-list-wrap">
        {loading ? (
          <p className="section-hint">Lädt…</p>
        ) : tasks.length === 0 ? (
          <div className="tasks-empty">
            <p>Keine Aufgaben in dieser Ansicht.</p>
            {scope === 'mine' && <p className="section-hint">Versuch's mit „Mein Team" — vielleicht hängt da etwas.</p>}
          </div>
        ) : (
          <ul className="tasks-list">
            {tasks.map(t => (
              <TaskCard
                key={t._id}
                task={t}
                currentUser={user}
                statusInfo={statusByKey[t.status] || FALLBACK_STATUS}
                statuses={statuses}
                onStatusChange={(s) => changeStatus(t._id, s)}
                onReassign={(body) => reassignTask(t._id, body)}
                onDelete={() => deleteTask(t._id)}
              />
            ))}
          </ul>
        )}
      </div>
      )}

      {showNewTask && (
        <NewTaskModal onClose={() => setShowNewTask(false)} onCreated={() => { setShowNewTask(false); load(); loadCounts(); }} />
      )}
      {detailTask && (
        <TaskDetailModal
          task={tasks.find(t => t._id === detailTask._id) || detailTask}
          currentUser={user}
          statuses={statuses}
          statusByKey={statusByKey}
          onClose={() => setDetailTask(null)}
          onReassign={(body) => reassignTask(detailTask._id, body)}
          onChangeStatus={(s) => changeStatus(detailTask._id, s)}
          onDelete={() => { deleteTask(detailTask._id); setDetailTask(null); }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, label, badge, hint }) {
  return (
    <button
      className={`tasks-tab ${active ? 'active' : ''}`}
      onClick={onClick}
      title={hint || ''}
    >
      {label}
      {badge > 0 && <span className="tasks-tab-badge">{badge}</span>}
    </button>
  );
}

function TaskCard({ task, currentUser, statusInfo, statuses, onStatusChange, onReassign, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const status = statusInfo || FALLBACK_STATUS;
  const isClosed = !!status.isFinal;
  const dueCls = dueClass(task.due, isClosed);
  const canDelete = task.createdBy === currentUser?._id || currentUser?.canViewAllTasks || currentUser?.role === 'admin';

  return (
    <li className={`tasks-card status-${task.status} ${isClosed ? 'closed' : ''}`}>
      <div className="tasks-card-head">
        <span className="tasks-card-status" style={{ color: status.color }}>
          {status.icon}
        </span>
        <button className="tasks-card-title" onClick={() => setExpanded(v => !v)}>
          {task.title}
        </button>
        <span className={`tasks-card-due ${dueCls}`}>
          {task.due ? `📅 ${formatDate(task.due)}` : ''}
        </span>
      </div>
      <div className="tasks-card-meta">
        <span className="tasks-card-assignee">
          {task.assigneeUserName ? `👤 ${task.assigneeUserName}`
            : task.assigneeDepartmentName ? `🏛 ${task.assigneeDepartmentName}`
            : '⚠ Nicht zugewiesen'}
        </span>
        {task.workflowName && (
          <span className="tasks-card-source" title="Quelle: Workflow">
            ⚡ {task.workflowName}
          </span>
        )}
        <span className="tasks-card-created">
          erstellt {formatDate(task.createdAt)}
        </span>
      </div>
      {expanded && (
        <div className="tasks-card-detail">
          {task.description && <p>{task.description}</p>}
          {task.payload && (
            <details>
              <summary>Payload anzeigen</summary>
              <pre className="tasks-card-payload">{JSON.stringify(task.payload, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
      <div className="tasks-card-actions-bar">
        <button
          type="button"
          className="tasks-card-actions-toggle"
          aria-expanded={actionsOpen}
          aria-label={actionsOpen ? 'Aktionen ausblenden' : 'Aktionen einblenden'}
          onClick={() => setActionsOpen(v => !v)}
        >
          {actionsOpen ? '▴' : '▾'} Aktionen
        </button>
      </div>
      {actionsOpen && (
        <div className="tasks-card-actions">
          <label className="tasks-card-status-select">
            Status:
            <select value={task.status} onChange={e => onStatusChange(e.target.value)}>
              {statuses.map(s => (
                <option key={s.key} value={s.key}>{s.icon} {s.label}</option>
              ))}
            </select>
          </label>
          <button className="link-btn" onClick={() => setReassigning(true)}>↪ Neu zuweisen</button>
          {canDelete && (
            <button className="link-btn danger" onClick={onDelete}>🗑️ Löschen</button>
          )}
        </div>
      )}
      {reassigning && (
        <ReassignModal
          task={task}
          onClose={() => setReassigning(false)}
          onSubmit={async (body) => { await onReassign(body); setReassigning(false); }}
        />
      )}
    </li>
  );
}

function NewTaskModal({ onClose, onCreated }) {
  const { tenantFetch, activeTenant } = useTenant();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [assigneeDepartmentId, setAssigneeDepartmentId] = useState('');
  const [due, setDue] = useState('');
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    tenantFetch('/api/departments').then(r => r.json()).then(d => setDepartments(d.departments || [])).catch(() => {});
    if (activeTenant?._id) {
      tenantFetch(`/api/tenants/${activeTenant._id}/members`)
        .then(r => r.json())
        .then(d => setMembers(d.members || []))
        .catch(() => {});
    }
  }, [tenantFetch, activeTenant]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Titel erforderlich.'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await tenantFetch('/api/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          assigneeUserId: assigneeUserId || null,
          assigneeDepartmentId: assigneeDepartmentId || null,
          due: due || null
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const topDepts = departments.filter(d => !d.parentId);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Neue Aufgabe</h3>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>
        <form className="modal-body" onSubmit={submit}>
          {error && <div className="error-message small">{error}</div>}
          <label className="modal-label">Titel</label>
          <input
            className="modal-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
          />
          <label className="modal-label">Beschreibung (optional)</label>
          <textarea
            className="modal-input"
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <label className="modal-label">Zuständige Person</label>
          <select
            className="modal-input"
            value={assigneeUserId}
            onChange={e => { setAssigneeUserId(e.target.value); if (e.target.value) setAssigneeDepartmentId(''); }}
            disabled={!!assigneeDepartmentId}
          >
            <option value="">— keine —</option>
            {members.map(m => <option key={m.userId} value={m.userId}>{m.username} ({m.email})</option>)}
          </select>
          <label className="modal-label">Zuständige Abteilung</label>
          <select
            className="modal-input"
            value={assigneeDepartmentId}
            onChange={e => { setAssigneeDepartmentId(e.target.value); if (e.target.value) setAssigneeUserId(''); }}
            disabled={!!assigneeUserId}
          >
            <option value="">— keine —</option>
            {topDepts.map(top => {
              const subs = departments.filter(d => d.parentId === top._id);
              return (
                <optgroup key={top._id} label={top.name}>
                  <option value={top._id}>{top.name} (Top-Ebene)</option>
                  {subs.map(s => <option key={s._id} value={s._id}>↳ {s.name}</option>)}
                </optgroup>
              );
            })}
          </select>
          <label className="modal-label">Fällig am</label>
          <input
            type="date"
            className="modal-input"
            value={due}
            onChange={e => setDue(e.target.value)}
          />
          <div className="modal-actions">
            <button type="submit" className="btn-sm-primary" disabled={busy}>
              {busy ? '…' : 'Anlegen'}
            </button>
            <button type="button" className="btn-sm-cancel" onClick={onClose}>Abbrechen</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Kanban-Board ───────────────────────────────────────────────────
// Spalten = Status des Mandanten (sortiert). Drag&Drop einer Karte zwischen
// Spalten löst onChangeStatus aus → PATCH /api/tasks/:id mit dem neuen Status.
// Native HTML5 Drag&Drop, keine externe Library.

function KanbanBoard({ tasks, statuses, loading, currentUser, onChangeStatus, onReassign, onDelete, onOpen }) {
  // Welche Spalte ist gerade Drop-Target? Für visuelles Feedback.
  const [dragOver, setDragOver] = useState(null);

  const grouped = statuses.reduce((acc, s) => {
    acc[s.key] = tasks.filter(t => t.status === s.key);
    return acc;
  }, {});

  const handleDragStart = (e, task) => {
    e.dataTransfer.setData('text/plain', task._id);
    e.dataTransfer.effectAllowed = 'move';
    // Wir setzen die aktuelle Status-Spalte mit, damit wir noOp erkennen
    e.dataTransfer.setData('application/x-task-from-status', task.status);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault(); // wichtig damit drop überhaupt feuert
    e.dataTransfer.dropEffect = 'move';
    if (dragOver !== status) setDragOver(status);
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    setDragOver(null);
    const taskId = e.dataTransfer.getData('text/plain');
    const fromStatus = e.dataTransfer.getData('application/x-task-from-status');
    if (!taskId || fromStatus === newStatus) return;
    onChangeStatus(taskId, newStatus);
  };

  return (
    <div className="kanban-wrap">
      {loading && tasks.length === 0 ? (
        <p className="section-hint">Lädt…</p>
      ) : (
        <div className="kanban-board">
          {statuses.map(info => {
            const key = info.key;
            const colTasks = grouped[key] || [];
            return (
              <div
                key={key}
                className={`kanban-column ${dragOver === key ? 'drop-target' : ''}`}
                onDragOver={e => handleDragOver(e, key)}
                onDragLeave={() => setDragOver(null)}
                onDrop={e => handleDrop(e, key)}
              >
                <div className="kanban-column-head" style={{ borderTopColor: info.color }}>
                  <span className="kanban-column-icon" style={{ color: info.color }}>{info.icon}</span>
                  <span className="kanban-column-label">{info.label}</span>
                  <span className="kanban-column-count">{colTasks.length}</span>
                </div>
                <div className="kanban-column-body">
                  {colTasks.length === 0 ? (
                    <div className="kanban-column-empty">—</div>
                  ) : (
                    colTasks.map(t => (
                      <KanbanCard
                        key={t._id}
                        task={t}
                        statusInfo={info}
                        statuses={statuses}
                        currentUser={currentUser}
                        onDragStart={handleDragStart}
                        onChangeStatus={onChangeStatus}
                        onReassign={onReassign}
                        onDelete={onDelete}
                        onOpen={onOpen}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KanbanCard({ task, statusInfo, statuses, currentUser, onDragStart, onChangeStatus, onReassign, onDelete, onOpen }) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const isFinal = !!(statusInfo && statusInfo.isFinal);
  const dueCls = dueClass(task.due, isFinal);
  const canDelete = task.createdBy === currentUser?._id || currentUser?.canViewAllTasks || currentUser?.role === 'admin';
  return (
    <div
      className="kanban-card"
      draggable
      onDragStart={e => onDragStart(e, task)}
      style={statusInfo ? { borderLeft: `3px solid ${statusInfo.color}` } : undefined}
    >
      <button
        type="button"
        className="kanban-card-title-btn"
        onClick={() => onOpen && onOpen(task)}
        title="Details öffnen"
      >
        {task.title}
      </button>
      <div className="kanban-card-meta">
        {task.assigneeUserName ? `👤 ${task.assigneeUserName}`
          : task.assigneeDepartmentName ? `🏛 ${task.assigneeDepartmentName}`
          : '⚠'}
      </div>
      {task.due && (
        <div className={`kanban-card-due ${dueCls}`}>📅 {formatDate(task.due)}</div>
      )}
      {task.workflowName && (
        <div className="kanban-card-workflow" title={`Aus Workflow: ${task.workflowName}`}>
          ⚡ {task.workflowName}
        </div>
      )}
      <div className="kanban-card-actions-bar">
        <button
          type="button"
          className="tasks-card-actions-toggle"
          aria-expanded={actionsOpen}
          onClick={() => setActionsOpen(v => !v)}
        >
          {actionsOpen ? '▴' : '▾'} Aktionen
        </button>
      </div>
      {actionsOpen && (
        <div className="kanban-card-actions">
          <label className="tasks-card-status-select">
            Status:
            <select value={task.status} onChange={e => onChangeStatus(task._id, e.target.value)}>
              {statuses.map(s => (
                <option key={s.key} value={s.key}>{s.icon} {s.label}</option>
              ))}
            </select>
          </label>
          <button className="link-btn" onClick={() => setReassigning(true)}>↪ Zuweisen</button>
          {canDelete && (
            <button className="link-btn danger" onClick={() => onDelete(task._id)}>🗑</button>
          )}
        </div>
      )}
      {reassigning && (
        <ReassignModal
          task={task}
          onClose={() => setReassigning(false)}
          onSubmit={async (body) => { await onReassign(task._id, body); setReassigning(false); }}
        />
      )}
    </div>
  );
}

// Detail-Modal: zeigt Titel, Beschreibung, Payload, Workflow-Quelle,
// Fälligkeit, Zuständigkeit und Status-Änderungs-Buttons. Wird sowohl
// aus Kanban-Karten als auch potentiell von List-Cards geöffnet.
function TaskDetailModal({ task, currentUser, statuses, statusByKey, onClose, onChangeStatus, onReassign, onDelete }) {
  const [reassigning, setReassigning] = useState(false);
  if (!task) return null;
  const status = (statusByKey && statusByKey[task.status]) || FALLBACK_STATUS;
  const canDelete = task.createdBy === currentUser?._id || currentUser?.canViewAllTasks || currentUser?.role === 'admin';
  const payloadStr = task.payload && typeof task.payload === 'object'
    ? JSON.stringify(task.payload, null, 2)
    : (task.payload ? String(task.payload) : '');

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: status.color }}>{status.icon}</span>
            {task.title}
          </h3>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="task-detail-grid">
            <div><strong>Status:</strong> <span style={{ color: status.color }}>{status.label}</span></div>
            <div><strong>Zuständig:</strong>{' '}
              {task.assigneeUserName ? `👤 ${task.assigneeUserName}`
                : task.assigneeDepartmentName ? `🏛 ${task.assigneeDepartmentName}`
                : '⚠ Niemand'}
            </div>
            <div><strong>Fällig am:</strong> {task.due ? formatDate(task.due) : '—'}</div>
            <div><strong>Erstellt:</strong> {formatDate(task.createdAt)}</div>
            {task.workflowName && (
              <div><strong>Aus Workflow:</strong> ⚡ {task.workflowName}</div>
            )}
            {task.closedAt && (
              <div><strong>Abgeschlossen:</strong> {formatDate(task.closedAt)}</div>
            )}
          </div>

          {task.description && (
            <>
              <label className="modal-label" style={{ marginTop: 12 }}>Beschreibung</label>
              <div className="task-detail-text">{task.description}</div>
            </>
          )}

          {payloadStr && (
            <>
              <label className="modal-label" style={{ marginTop: 12 }}>Payload</label>
              <pre className="task-detail-payload">{payloadStr}</pre>
            </>
          )}
        </div>
        <div className="modal-actions" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <label className="tasks-card-status-select">
            Status:
            <select value={task.status} onChange={e => onChangeStatus(e.target.value)}>
              {(statuses || []).map(s => (
                <option key={s.key} value={s.key}>{s.icon} {s.label}</option>
              ))}
            </select>
          </label>
          <button className="link-btn" onClick={() => setReassigning(true)}>↪ Neu zuweisen</button>
          {canDelete && (
            <button className="link-btn danger" onClick={onDelete}>🗑 Löschen</button>
          )}
          <button className="btn-sm-cancel" onClick={onClose}>Schließen</button>
        </div>
        {reassigning && (
          <ReassignModal
            task={task}
            onClose={() => setReassigning(false)}
            onSubmit={async (body) => { await onReassign(body); setReassigning(false); }}
          />
        )}
      </div>
    </div>
  );
}

function ReassignModal({ task, onClose, onSubmit }) {
  const { tenantFetch, activeTenant } = useTenant();
  const [mode, setMode] = useState(task.assigneeDepartmentId ? 'department' : (task.assigneeUserId ? 'user' : 'user'));
  const [assigneeUserId, setAssigneeUserId] = useState(task.assigneeUserId || '');
  const [assigneeDepartmentId, setAssigneeDepartmentId] = useState(task.assigneeDepartmentId || '');
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    tenantFetch('/api/departments').then(r => r.json()).then(d => setDepartments(d.departments || [])).catch(() => {});
    if (activeTenant?._id) {
      tenantFetch(`/api/tenants/${activeTenant._id}/members`)
        .then(r => r.json())
        .then(d => setMembers(d.members || []))
        .catch(() => {});
    }
  }, [tenantFetch, activeTenant]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    let body;
    if (mode === 'user') {
      if (!assigneeUserId) { setError('Bitte eine Person wählen.'); return; }
      body = { assigneeUserId, assigneeDepartmentId: null };
    } else if (mode === 'department') {
      if (!assigneeDepartmentId) { setError('Bitte eine Abteilung wählen.'); return; }
      body = { assigneeUserId: null, assigneeDepartmentId };
    } else {
      body = { assigneeUserId: null, assigneeDepartmentId: null };
    }
    setBusy(true);
    try {
      await onSubmit(body);
    } catch (err) {
      setError(err.message || 'Fehler');
    } finally {
      setBusy(false);
    }
  };

  const topDepts = departments.filter(d => !d.parentId);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Aufgabe neu zuweisen</h3>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>
        <form className="modal-body" onSubmit={submit}>
          {error && <div className="error-message small">{error}</div>}
          <div className="reassign-mode-tabs">
            <label><input type="radio" checked={mode === 'user'} onChange={() => setMode('user')} /> Person</label>
            <label><input type="radio" checked={mode === 'department'} onChange={() => setMode('department')} /> Abteilung</label>
            <label><input type="radio" checked={mode === 'none'} onChange={() => setMode('none')} /> Niemand</label>
          </div>
          {mode === 'user' && (
            <>
              <label className="modal-label">Zuständige Person</label>
              <select className="modal-input" value={assigneeUserId} onChange={e => setAssigneeUserId(e.target.value)}>
                <option value="">— wählen —</option>
                {members.map(m => <option key={m.userId} value={m.userId}>{m.username} ({m.email})</option>)}
              </select>
            </>
          )}
          {mode === 'department' && (
            <>
              <label className="modal-label">Zuständige Abteilung</label>
              <select className="modal-input" value={assigneeDepartmentId} onChange={e => setAssigneeDepartmentId(e.target.value)}>
                <option value="">— wählen —</option>
                {topDepts.map(top => {
                  const subs = departments.filter(d => d.parentId === top._id);
                  return (
                    <optgroup key={top._id} label={top.name}>
                      <option value={top._id}>{top.name} (Top-Ebene)</option>
                      {subs.map(s => <option key={s._id} value={s._id}>↳ {s.name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            </>
          )}
          <div className="modal-actions">
            <button type="submit" className="btn-sm-primary" disabled={busy}>{busy ? '…' : 'Zuweisen'}</button>
            <button type="button" className="btn-sm-cancel" onClick={onClose}>Abbrechen</button>
          </div>
        </form>
      </div>
    </div>
  );
}
